import eip55 from "eip55";
import BigNumber from "bignumber.js";
import { encodeERC1155OperationId, encodeERC721OperationId, } from "@ledgerhq/coin-framework/nft/nftOperationId";
import { encodeNftId } from "@ledgerhq/coin-framework/nft/nftId";
import { encodeOperationId } from "@ledgerhq/coin-framework/operation";
import { findSubAccountById } from "@ledgerhq/coin-framework/account/index";
import { getEstimatedFees, isNftTransaction } from "./logic";
/**
 * Build an optimistic operation for the coin of the integration (e.g. Ether for Ethereum)
 */
export const buildOptimisticCoinOperation = (account, transaction, transactionType) => {
    const type = transactionType !== null && transactionType !== void 0 ? transactionType : "OUT";
    const estimatedFees = getEstimatedFees(transaction);
    const value = transaction.amount.plus(estimatedFees);
    // keys marked with a <-- will be updated by the broadcast method
    const operation = {
        id: encodeOperationId(account.id, "", type),
        hash: "",
        type,
        value,
        fee: estimatedFees,
        blockHash: null,
        blockHeight: null,
        senders: [eip55.encode(account.freshAddress)],
        recipients: [eip55.encode(transaction.recipient)],
        accountId: account.id,
        transactionSequenceNumber: transaction.nonce,
        date: new Date(),
        extra: {},
    };
    return operation;
};
/**
 * Build an optimistic operation for an ERC20 transaction
 */
export const buildOptimisticTokenOperation = (account, tokenAccount, transaction) => {
    const type = "OUT";
    const estimatedFees = getEstimatedFees(transaction);
    const value = transaction.useAllAmount ? tokenAccount.balance : transaction.amount;
    const coinOperation = buildOptimisticCoinOperation(account, Object.assign(Object.assign({}, transaction), { recipient: tokenAccount.token.contractAddress, amount: new BigNumber(0) }), "FEES");
    // keys marked with a <-- will be updated by the broadcast method
    const operation = Object.assign(Object.assign({}, coinOperation), { subOperations: [
            {
                id: encodeOperationId(tokenAccount.id, "", type),
                hash: "",
                type,
                value,
                fee: estimatedFees,
                blockHash: null,
                blockHeight: null,
                senders: [eip55.encode(account.freshAddress)],
                recipients: [eip55.encode(transaction.recipient)],
                accountId: tokenAccount.id,
                transactionSequenceNumber: transaction.nonce,
                date: new Date(),
                extra: {},
                contract: tokenAccount.token.contractAddress,
            },
        ] });
    return operation;
};
/**
 * Build an optimistic operation for an ERC721 or ERC1155 transaction
 */
export const buildOptimisticNftOperation = (account, transaction) => {
    const type = "NFT_OUT";
    const estimatedFees = getEstimatedFees(transaction);
    const { nft } = transaction;
    const value = nft.quantity;
    const coinOperation = buildOptimisticCoinOperation(account, Object.assign(Object.assign({}, transaction), { recipient: nft.contract, amount: new BigNumber(0) }), "FEES");
    const nftId = encodeNftId(account.id, nft.contract, nft.tokenId, account.currency.id);
    const nftOpId = transaction.mode === "erc721"
        ? encodeERC721OperationId(nftId, "", type, 0)
        : encodeERC1155OperationId(nftId, "", type, 0);
    // keys marked with a <-- will be updated by the broadcast method
    const operation = Object.assign(Object.assign({}, coinOperation), { nftOperations: [
            {
                id: nftOpId,
                hash: "",
                type,
                value,
                fee: estimatedFees,
                blockHash: null,
                blockHeight: null,
                senders: [eip55.encode(account.freshAddress)],
                recipients: [eip55.encode(transaction.recipient)],
                accountId: account.id,
                transactionSequenceNumber: transaction.nonce,
                date: new Date(),
                extra: {},
                contract: nft.contract,
                standard: transaction.mode.toUpperCase(),
                tokenId: nft.tokenId,
            },
        ] });
    return operation;
};
/**
 * Create a temporary operation to use until it's confirmed by the blockchain
 */
export const buildOptimisticOperation = (account, transaction) => {
    if (isNftTransaction(transaction)) {
        return buildOptimisticNftOperation(account, transaction);
    }
    const subAccount = findSubAccountById(account, (transaction === null || transaction === void 0 ? void 0 : transaction.subAccountId) || "");
    const isTokenTransaction = (subAccount === null || subAccount === void 0 ? void 0 : subAccount.type) === "TokenAccount";
    return isTokenTransaction
        ? buildOptimisticTokenOperation(account, subAccount, transaction)
        : buildOptimisticCoinOperation(account, transaction);
};
export default buildOptimisticOperation;
//# sourceMappingURL=buildOptimisticOperation.js.map